import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardList, Columns3, List, Pencil, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useDeleteTask, useTasks, useUsers } from '../hooks/useApi';
import TaskModal from '../components/TaskModal';
import { canManageTasks } from '../utils/permissions';
import {
    Avatar,
    Button,
    Card,
    ConfirmDialog,
    DataTable,
    Dropdown,
    EmptyState,
    MenuItem,
    PageHeader,
    Pagination,
    PriorityBadge,
    SegmentedControl,
    StatusBadge,
    TableToolbar,
    useSort,
} from '../components/ui';

const KANBAN_COLUMNS = [
    { key: 'todo', label: 'To do', accent: '#a7aebd' },
    { key: 'in_progress', label: 'In progress', accent: '#2589c9' },
    { key: 'blocked', label: 'Blocked', accent: '#dc4c3f' },
    { key: 'done', label: 'Done', accent: '#0f9d76' },
    { key: 'cancelled', label: 'Cancelled', accent: '#a7aebd' },
];

const formatDate = (value) => {
    if (!value || value === '0000-00-00' || value === '1970-01-01') return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime()) || d.getFullYear() === 1970) return '—';
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const isOverdue = (task) => {
    if (!task.due_date || task.status === 'done') return false;
    const d = new Date(task.due_date);
    return !Number.isNaN(d.getTime()) && d < new Date(new Date().toDateString());
};

const Tasks = () => {
    const { user: currentUser } = useAuth();
    const navigate = useNavigate();

    const [filters, setFilters] = useState({});
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [view, setView] = useState('list');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [taskToDelete, setTaskToDelete] = useState(null);

    const { data: result, isLoading } = useTasks({ limit: 1000 });
    const allTasks = useMemo(() => result?.data || [], [result]);

    const { data: usersResult } = useUsers({ limit: 1000 });
    const users = usersResult?.data || [];

    const deleteTask = useDeleteTask();
    const userCanManage = canManageTasks(currentUser);
    const { sort, onSort } = useSort({ key: 'due_date', dir: 'asc' });

    const filteredTasks = useMemo(() => {
        const term = search.trim().toLowerCase();

        const rows = allTasks.filter((task) => {
            if (filters.my_tasks === 'true' && task.assignee_id !== currentUser?.id) return false;
            if (filters.status && task.status !== filters.status) return false;
            if (filters.priority && task.priority !== filters.priority) return false;
            if (filters.assignee_id && String(task.assignee_id) !== String(filters.assignee_id)) return false;
            if (term) {
                const haystack = [task.title, task.task_code, task.project_name, task.assignee_name];
                if (!haystack.some((v) => v?.toLowerCase().includes(term))) return false;
            }
            return true;
        });

        if (sort) {
            const dir = sort.dir === 'asc' ? 1 : -1;
            rows.sort((a, b) => {
                const av = a[sort.key];
                const bv = b[sort.key];
                if (sort.key.endsWith('_date')) {
                    return ((new Date(av || 0).getTime() || 0) - (new Date(bv || 0).getTime() || 0)) * dir;
                }
                if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
                return String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true }) * dir;
            });
        }
        return rows;
    }, [allTasks, filters, search, currentUser, sort]);

    const totalPages = Math.max(1, Math.ceil(filteredTasks.length / pageSize));
    const currentPage = Math.min(page, totalPages);
    const pagedTasks = filteredTasks.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const handleFilterChange = (name, value) => {
        setFilters((prev) => ({ ...prev, [name]: value }));
        setPage(1);
    };

    const openCreate = () => {
        setSelectedTask(null);
        setIsModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!taskToDelete) return;
        try {
            await deleteTask.mutateAsync(taskToDelete);
            setTaskToDelete(null);
        } catch (error) {
            console.error('Error deleting task:', error);
        }
    };

    const hasFilters = Boolean(
        search || filters.status || filters.priority || filters.assignee_id || filters.my_tasks
    );

    const columns = [
        {
            key: 'title',
            header: 'Task',
            sortable: true,
            render: (t) => (
                <Link to={`/tasks/${t.id}`} className="block group" onClick={(e) => e.stopPropagation()}>
                    <span className="block font-medium text-ink-900 group-hover:text-primary-600 transition-colors duration-150 truncate max-w-[300px]">
                        {t.title}
                    </span>
                    {t.task_code && (
                        <span className="block text-micro text-ink-500 font-mono mt-0.5">{t.task_code}</span>
                    )}
                </Link>
            ),
        },
        {
            key: 'project_name',
            header: 'Project',
            sortable: true,
            render: (t) => <span className="text-ink-700 truncate block max-w-[180px]">{t.project_name || '—'}</span>,
        },
        {
            key: 'assignee_name',
            header: 'Assignee',
            sortable: true,
            render: (t) =>
                t.assignee_name ? (
                    <span className="flex items-center gap-2">
                        <Avatar name={t.assignee_name} size="sm" />
                        <span className="text-ink-700 truncate max-w-[130px]">{t.assignee_name}</span>
                    </span>
                ) : (
                    <span className="text-muted">Unassigned</span>
                ),
        },
        {
            key: 'priority',
            header: 'Priority',
            sortable: true,
            render: (t) => <PriorityBadge priority={t.priority} />,
        },
        {
            key: 'status',
            header: 'Status',
            sortable: true,
            render: (t) => <StatusBadge status={t.status} />,
        },
        {
            key: 'due_date',
            header: 'Due',
            sortable: true,
            render: (t) => (
                <span
                    className={`tabular whitespace-nowrap ${isOverdue(t) ? 'text-danger font-medium' : 'text-ink-600'}`}
                    title={isOverdue(t) ? 'Past due' : undefined}
                >
                    {formatDate(t.due_date)}
                </span>
            ),
        },
    ];

    if (userCanManage) {
        columns.push({
            key: 'actions',
            header: '',
            align: 'right',
            width: 48,
            render: (t) => (
                <div onClick={(e) => e.stopPropagation()}>
                    <Dropdown label={`Actions for ${t.title}`}>
                        <MenuItem
                            icon={Pencil}
                            onClick={() => {
                                setSelectedTask(t);
                                setIsModalOpen(true);
                            }}
                        >
                            Edit task
                        </MenuItem>
                        <MenuItem icon={Trash2} danger onClick={() => setTaskToDelete(t.id)}>
                            Delete task
                        </MenuItem>
                    </Dropdown>
                </div>
            ),
        });
    }

    return (
        <>
            <PageHeader
                title="Tasks"
                description="Every work item across projects, with owner, priority and due date."
                breadcrumbs={[{ label: 'Home', to: '/' }, { label: 'Tasks' }]}
                actions={
                    <>
                        <SegmentedControl
                            label="Task view"
                            options={[
                                { value: 'list', label: 'List', icon: List },
                                { value: 'kanban', label: 'Board', icon: Columns3 },
                            ]}
                            value={view}
                            onChange={setView}
                        />
                        {userCanManage && (
                            <Button variant="primary" icon={Plus} onClick={openCreate}>
                                New task
                            </Button>
                        )}
                    </>
                }
            />

            <Card>
                <TableToolbar
                    search={search}
                    onSearchChange={(v) => {
                        setSearch(v);
                        setPage(1);
                    }}
                    searchPlaceholder="Search title, code, project…"
                    filters={
                        <>
                            <select
                                className="input w-auto"
                                aria-label="Filter by status"
                                value={filters.status || ''}
                                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                            >
                                <option value="">All statuses</option>
                                <option value="todo">To do</option>
                                <option value="in_progress">In progress</option>
                                <option value="blocked">Blocked</option>
                                <option value="done">Done</option>
                                <option value="cancelled">Cancelled</option>
                            </select>

                            <select
                                className="input w-auto"
                                aria-label="Filter by priority"
                                value={filters.priority || ''}
                                onChange={(e) => handleFilterChange('priority', e.target.value || undefined)}
                            >
                                <option value="">All priorities</option>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="critical">Critical</option>
                            </select>

                            <select
                                className="input w-auto"
                                aria-label="Filter by assignee"
                                value={filters.assignee_id || ''}
                                onChange={(e) => handleFilterChange('assignee_id', e.target.value || undefined)}
                            >
                                <option value="">All assignees</option>
                                {users.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {u.first_name} {u.last_name}
                                    </option>
                                ))}
                            </select>

                            <label className="inline-flex items-center gap-1.5 text-base text-ink-700 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    className="w-3.5 h-3.5 accent-[var(--primary)]"
                                    checked={filters.my_tasks === 'true'}
                                    onChange={(e) =>
                                        handleFilterChange('my_tasks', e.target.checked ? 'true' : undefined)
                                    }
                                />
                                My tasks
                            </label>
                        </>
                    }
                />

                {view === 'list' ? (
                    <>
                        <DataTable
                            columns={columns}
                            rows={pagedTasks}
                            loading={isLoading}
                            sort={sort}
                            onSort={onSort}
                            onRowClick={(t) => navigate(`/tasks/${t.id}`)}
                            empty={
                                <EmptyState
                                    icon={ClipboardList}
                                    title={hasFilters ? 'No tasks match these filters' : 'No tasks yet'}
                                    description={
                                        hasFilters
                                            ? 'Clear the search or filters to see everything.'
                                            : 'Create a task to start tracking work.'
                                    }
                                    action={
                                        hasFilters ? (
                                            <Button
                                                onClick={() => {
                                                    setSearch('');
                                                    setFilters({});
                                                }}
                                            >
                                                Clear filters
                                            </Button>
                                        ) : (
                                            userCanManage && (
                                                <Button variant="primary" icon={Plus} onClick={openCreate}>
                                                    New task
                                                </Button>
                                            )
                                        )
                                    }
                                />
                            }
                        />
                        {filteredTasks.length > 0 && (
                            <Pagination
                                page={currentPage}
                                totalPages={totalPages}
                                total={filteredTasks.length}
                                pageSize={pageSize}
                                onPageChange={setPage}
                                onPageSizeChange={(n) => {
                                    setPageSize(n);
                                    setPage(1);
                                }}
                            />
                        )}
                    </>
                ) : (
                    <div className="p-4 overflow-x-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-3 min-w-[280px]">
                            {KANBAN_COLUMNS.map((col) => {
                                const items = filteredTasks.filter((t) => t.status === col.key);
                                return (
                                    <section
                                        key={col.key}
                                        className="rounded-md bg-[var(--bg-subtle)] border border-[var(--border-color)] flex flex-col"
                                    >
                                        <header className="flex items-center gap-2 px-3 py-2.5 border-b border-[var(--border-color)]">
                                            <span
                                                aria-hidden="true"
                                                className="w-1.5 h-1.5 rounded-full"
                                                style={{ backgroundColor: col.accent }}
                                            />
                                            <h3 className="text-label font-semibold text-ink-800">{col.label}</h3>
                                            <span className="ml-auto text-micro text-ink-500 tabular">
                                                {items.length}
                                            </span>
                                        </header>

                                        <div className="p-2 space-y-2 min-h-[80px]">
                                            {items.length === 0 && (
                                                <p className="text-label text-ink-400 text-center py-5">
                                                    Nothing here
                                                </p>
                                            )}
                                            {items.map((task) => (
                                                <Link
                                                    key={task.id}
                                                    to={`/tasks/${task.id}`}
                                                    className="block bg-white border border-[var(--border-color)] rounded-md p-2.5 hover:border-[var(--border-strong)] hover:shadow-xs transition-all duration-150"
                                                >
                                                    <p className="text-base font-medium text-ink-900 line-clamp-2">
                                                        {task.title}
                                                    </p>
                                                    {task.project_name && (
                                                        <p className="text-micro text-ink-500 truncate mt-0.5">
                                                            {task.project_name}
                                                        </p>
                                                    )}
                                                    <div className="flex items-center gap-2 mt-2.5">
                                                        <PriorityBadge priority={task.priority} />
                                                        <span
                                                            className={`ml-auto text-micro tabular ${isOverdue(task) ? 'text-danger font-medium' : 'text-ink-500'
                                                                }`}
                                                        >
                                                            {formatDate(task.due_date)}
                                                        </span>
                                                        {task.assignee_name && (
                                                            <Avatar name={task.assignee_name} size="xs" />
                                                        )}
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    </section>
                                );
                            })}
                        </div>
                    </div>
                )}
            </Card>

            {isModalOpen && <TaskModal task={selectedTask} onClose={() => setIsModalOpen(false)} />}

            <ConfirmDialog
                isOpen={Boolean(taskToDelete)}
                title="Delete task"
                message="This permanently removes the task and its comments. This cannot be undone."
                confirmText="Delete task"
                variant="danger"
                isLoading={deleteTask.isPending}
                onConfirm={confirmDelete}
                onClose={() => setTaskToDelete(null)}
            />
        </>
    );
};

export default Tasks;
